import { supabase } from './supabase'
import { createNotification, logActivity, resolveNotificationsFor, Actor } from './activity'

// =====================================================================
// Consulting-credits engine.
//
// Rule (from the owner): every 2 APPROVED tutoring sessions a student
// completes grants 1 free consulting session. Only approved, non-cancelled
// tutoring sessions count. Consulting sessions themselves never count toward
// earning more credits (no feedback loop).
//
// syncConsultingCredits() is IDEMPOTENT — it reconciles the number of
// non-revoked credits to the entitlement derived from the live session count:
//   - grants credits that are owed but missing
//   - revokes SURPLUS *unclaimed* credits if entitlement drops (e.g. an
//     approved session is later cancelled/deleted/rejected)
//   - never revokes a credit that has already been claimed; instead it flags
//     the admin for manual review if claimed credits exceed entitlement.
//
// Safe to call after every approve / reject / cancel / delete of a session.
// =====================================================================

export const CONSULTING_RATIO = 2 // approved tutoring sessions per 1 free consulting session

/** Count approved, non-cancelled TUTORING sessions for a student. */
async function countQualifyingSessions(studentId: string): Promise<number> {
  const { count, error } = await supabase
    .from('sessions')
    .select('id', { count: 'exact', head: true })
    .eq('student_id', studentId)
    .eq('session_type', 'tutoring')
    .eq('approval_status', 'approved')
    .neq('lifecycle_status', 'cancelled')
  if (error) throw error
  return count ?? 0
}

export type SyncResult = { entitled: number; granted: number; revoked: number; claimedOverflow: number }

/**
 * Reconcile a student's consulting credits to their current entitlement.
 * Returns a summary of what changed. Best-effort on the notification/activity
 * side; the core credit writes are awaited so callers see consistent state.
 */
export async function syncConsultingCredits(
  studentId: string,
  opts?: { actor?: Actor; studentName?: string }
): Promise<SyncResult> {
  const actor = opts?.actor
  const studentName = opts?.studentName ?? 'Student'

  const approved = await countQualifyingSessions(studentId)
  const entitled = Math.floor(approved / CONSULTING_RATIO)

  const { data: existingRows, error } = await supabase
    .from('consulting_credits')
    .select('*')
    .eq('student_id', studentId)
    .neq('status', 'revoked')
    .order('earned_at', { ascending: true })
  if (error) throw error

  const current = existingRows ?? []
  let granted = 0
  let revoked = 0
  let claimedOverflow = 0

  if (entitled > current.length) {
    // ---- Grant missing credits ----
    const toAdd = entitled - current.length
    const rows = Array.from({ length: toAdd }, (_, i) => ({
      student_id: studentId,
      earned_at_session_count: (current.length + i + 1) * CONSULTING_RATIO,
      status: 'available' as const,
    }))
    const { error: insErr } = await supabase.from('consulting_credits').insert(rows)
    if (insErr) throw insErr
    granted = toAdd

    await createNotification({
      type: 'consulting_followup',
      title: `${studentName} earned ${toAdd} free consulting session${toAdd > 1 ? 's' : ''}`,
      message: `Reached ${approved} approved sessions. Follow up to offer the consulting session${toAdd > 1 ? 's' : ''}.`,
      relatedTable: 'students',
      relatedId: studentId,
      route: `/dashboard/students/${studentId}`,
      severity: 'warning',
    })
    await logActivity({
      scope: 'students',
      action: 'consulting_earned',
      summary: `${studentName} earned ${toAdd} consulting credit${toAdd > 1 ? 's' : ''} (${approved} approved sessions)`,
      entityTable: 'students',
      entityId: studentId,
      metadata: { approved, entitled, granted: toAdd },
      actor,
    })
  } else if (entitled < current.length) {
    // ---- Revoke surplus, UNCLAIMED credits only (newest first) ----
    const surplus = current.length - entitled
    const unclaimed = current.filter((c) => c.status === 'available').slice(-surplus)

    for (const c of unclaimed) {
      const { error: rvErr } = await supabase
        .from('consulting_credits')
        .update({
          status: 'revoked',
          revoked_at: new Date().toISOString(),
          revoked_reason: 'Entitlement dropped — a qualifying session was reversed',
        })
        .eq('id', c.id)
        .eq('status', 'available') // guard against a race claiming it meanwhile
      if (rvErr) throw rvErr
      revoked++
    }

    if (revoked > 0) {
      await logActivity({
        scope: 'students',
        action: 'consulting_revoked',
        summary: `${revoked} unclaimed consulting credit${revoked > 1 ? 's' : ''} revoked for ${studentName}`,
        entityTable: 'students',
        entityId: studentId,
        metadata: { approved, entitled, revoked },
        actor,
      })
    }

    // Some surplus couldn't be revoked because those credits were already
    // claimed. Don't auto-revoke a delivered session — flag for the admin.
    claimedOverflow = surplus - revoked
    if (claimedOverflow > 0) {
      await createNotification({
        type: 'consulting_followup',
        title: `Review consulting credits for ${studentName}`,
        message: `${claimedOverflow} already-claimed consulting session${claimedOverflow > 1 ? 's' : ''} now exceed entitlement after a session was reversed.`,
        relatedTable: 'students',
        relatedId: studentId,
        route: `/dashboard/students/${studentId}`,
        severity: 'alert',
      })
    }
  }

  return { entitled, granted, revoked, claimedOverflow }
}

/**
 * Record an admin's follow-up decision on a credit (accept / decline).
 * Per the owner's rule, a DECLINED credit stays `available` (reclaimable later) —
 * we only stamp the follow-up status/who/when.
 */
export async function setCreditFollowup(
  creditId: string,
  decision: 'accepted' | 'declined',
  opts?: { actor?: Actor; studentId?: string; studentName?: string }
): Promise<void> {
  const { error } = await supabase
    .from('consulting_credits')
    .update({
      followup_status: decision,
      followup_at: new Date().toISOString(),
      followup_by: opts?.actor?.email ?? null,
    })
    .eq('id', creditId)
  if (error) throw error

  await logActivity({
    scope: 'students',
    action: `consulting_${decision}`,
    summary: `Consulting follow-up ${decision} for ${opts?.studentName ?? 'student'}`,
    entityTable: 'consulting_credits',
    entityId: creditId,
    // Tag the student so this event surfaces in the per-student activity feed
    // (which matches on entity_id = studentId OR metadata.studentId).
    metadata: opts?.studentId ? { studentId: opts.studentId } : undefined,
    actor: opts?.actor,
  })

  // Once the admin has actioned the follow-up, clear the student's pending
  // consulting alert (they can still claim later from the student page).
  if (opts?.studentId) {
    await resolveNotificationsFor('students', opts.studentId, opts?.actor?.email, 'consulting_followup')
  }
}
